import { Button, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Tooltip2 } from '@blueprintjs/popover2';
import * as React from 'react';

import { components } from 'src/ui/templates/copy';
import { copyToClipboard } from 'src/ui/utils/copyToClipboard';

interface CopyToClipboardButtonProps {
  textToCopy: string;
  /* the amount of time in milliseconds to reset button's text to default text after successful/failed copy */
  resetTimeMs?: number;
  /* tooltip text to display on hover */
  tooltip?: string;
  /* custom text for the button */
  buttonText?: string;
  /* render as minimal icon-only button */
  minimal?: boolean;
  /* test ID for the button */
  testId?: string;
}

export const CopyToClipboardButton: React.VFC<CopyToClipboardButtonProps> = ({
  textToCopy,
  resetTimeMs = 5000,
  tooltip,
  buttonText,
  minimal = false,
  testId,
}) => {
  const [text, setText] = React.useState<string>(
    buttonText || components.copyToClipboardButton.initialText
  );
  const [intent, setIntent] = React.useState<Intent>(Intent.NONE);

  React.useEffect(() => {
    const initialText =
      buttonText || components.copyToClipboardButton.initialText;
    // no need to swap the text if text hasn't changed from its initial state
    if (text === initialText) {
      // looks weird to return undefined here but because we return a cleanup function later
      // we need to return something to have a consistent return
      return undefined;
    }

    const timer = setTimeout(() => {
      setText(initialText);
      setIntent(Intent.NONE);
    }, resetTimeMs);

    // if the component unmounts before setTimeout is finished,
    // we need to cleanup the timer to avoid memory leaks
    return () => clearTimeout(timer);
  }, [text, resetTimeMs, buttonText]);

  return (
    <Tooltip2 disabled={!tooltip} content={tooltip}>
      <Button
        className="sd-copy-to-clipboard-button"
        outlined={!minimal}
        minimal={minimal}
        small={minimal}
        intent={intent}
        icon={minimal ? IconNames.CLIPBOARD : undefined}
        rightIcon={
          !minimal &&
          text === (buttonText || components.copyToClipboardButton.initialText)
            ? IconNames.CLIPBOARD
            : false
        }
        text={minimal ? undefined : text}
        data-testid={testId}
        onClick={() => {
          copyToClipboard({ value: textToCopy }).then((copied) => {
            if (!copied) {
              setText(components.copyToClipboardButton.failureText);
              setIntent(Intent.WARNING);
              return;
            }
            setText(components.copyToClipboardButton.successText);
            setIntent(Intent.SUCCESS);
          });
        }}
      />
    </Tooltip2>
  );
};
